/**
 * Address derivation from a BIP-39 mnemonic.
 *
 * ZcashWallet is ZEC-only. The primary surface for the rest of the app
 * is the shielded UFVK + Unified Address (u1…), which lives in
 * src/shielded/ and is derived via ZIP-32 inside @chainsafe/webzjs-keys
 * (wired up in Phase 5).
 *
 * This file still carries transparent-receiver helpers (t1…) because
 * the keystore module uses them at create-time as a low-cost sanity
 * value to display until the WebZjs WASM loads. They will be replaced
 * by the Unified Address once derivation lands.
 */

import { HDKey } from "@scure/bip32";
import { secp256k1 } from "@noble/curves/secp256k1.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { ripemd160 } from "@noble/hashes/legacy.js";

import { PATHS } from "./derivation-paths";
import { phraseToSeed } from "./mnemonic";

/** Returns the 33-byte compressed secp256k1 public key for a given HD path. */
const derivePubkey = (seed: Uint8Array, path: string): Uint8Array => {
  const node = HDKey.fromMasterSeed(seed).derive(path);
  if (!node.privateKey) throw new Error(`No private key at ${path}`);
  return secp256k1.getPublicKey(node.privateKey, true);
};

/** Returns the raw 32-byte private key for a given HD path. */
export const derivePrivateKey = (seed: Uint8Array, path: string): Uint8Array => {
  const node = HDKey.fromMasterSeed(seed).derive(path);
  if (!node.privateKey) throw new Error(`No private key at ${path}`);
  return node.privateKey;
};

const BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

const base58Encode = (data: Uint8Array): string => {
  let num = 0n;
  for (const byte of data) num = num * 256n + BigInt(byte);
  let out = "";
  while (num > 0n) {
    const r = Number(num % 58n);
    out = BASE58_ALPHABET[r] + out;
    num = num / 58n;
  }
  for (const byte of data) {
    if (byte === 0) out = BASE58_ALPHABET[0] + out;
    else break;
  }
  return out;
};

/** Base58Check encode with a two-byte version prefix (Zcash transparent). */
const base58CheckEncodeZcash = (payload: Uint8Array, versionBytes: number[]): string => {
  const versioned = new Uint8Array(versionBytes.length + payload.length);
  versioned.set(versionBytes, 0);
  versioned.set(payload, versionBytes.length);

  const checksum = sha256(sha256(versioned)).slice(0, 4);

  const withChecksum = new Uint8Array(versioned.length + 4);
  withChecksum.set(versioned, 0);
  withChecksum.set(checksum, versioned.length);

  return base58Encode(withChecksum);
};

// ─── ZEC transparent (t1…) ───────────────────────────────────────────────────

/**
 * Derive the Zcash transparent P2PKH address (t1…) from a mnemonic.
 * Uses the two-byte mainnet prefix [0x1c, 0xb8] + base58check.
 *
 * Used at wallet-create time as a quick sanity value. The Unified
 * Address (u1…), which is what users actually share, is derived via
 * the shielded surface in Phase 5.
 */
export const deriveZcashAddress = (phrase: string): string => {
  const seed = phraseToSeed(phrase);
  const pubkey = derivePubkey(seed, PATHS.zcash);
  const pubKeyHash = ripemd160(sha256(pubkey));
  return base58CheckEncodeZcash(pubKeyHash, [0x1c, 0xb8]);
};

// ─── Bundle for storage layer ────────────────────────────────────────────────

export type WalletAddresses = {
  /** Transparent receiver, derived locally from the BIP-39 seed. */
  zecTransparent: string;
  /** Unified Address (u1…). Populated by the shielded module once the
   *  UFVK derivation + /wallet/address round-trip complete. Empty
   *  string until Phase 5 wires that up. */
  zecUnified: string;
};

/**
 * Synchronous derivation. Returns the transparent receiver (which we
 * can compute locally from the BIP-39 seed without loading WASM) and
 * a placeholder for the Unified Address. The Unified Address is
 * populated asynchronously by the ShieldedKeysProvider once the
 * Sapling signer WASM has loaded and run derive_default_unified_address.
 */
export const deriveAllAddresses = (phrase: string): WalletAddresses => ({
  zecTransparent: deriveZcashAddress(phrase),
  zecUnified: "",
});
